import { lazy } from 'react'
import { Navigate } from 'react-router-dom'

import { GetProcessDetailDocument, MaterializedProcessDocument } from 'generated/resources/graphql'
import { capitalize } from 'lodash'

import i18n from 'modules/core/lib/i18n'
import { MetaContext } from 'modules/core/lib/navigation/routes'
import { type NavigationRoutesFn, buildModuleRoute } from 'modules/core/lib/react-router'

import { ProcessResourceHeader } from 'modules/processes/components/ProcessResourceHeader'
import surveysRoutes from 'modules/surveys/routes'

const CategoryLayout = lazy(
  () => import('modules/processes/pages/Processes/ProcessesHomePage/Categories')
)

const ItemDetailPage = lazy(
  () => import('modules/processes/pages/MaterializedProcess/ItemDetailPage')
)
const MaterializedProcessPage = lazy(
  () => import('modules/processes/pages/MaterializedProcess/MaterializedProcessPage')
)
const AddStepModalPage = lazy(() => import('modules/processes/pages/Modals/AddStepModalPage'))
const CompletedMaterializedProcessPage = lazy(
  () => import('modules/processes/pages/Modals/CompletedMaterializedProcessPage')
)
const CreateCustomProcessModalPage = lazy(
  () => import('modules/processes/pages/Modals/CreateCustomProcessModalPage')
)
const DeleteMaterializedProcessModalPage = lazy(
  () => import('modules/processes/pages/Modals/DeleteMaterializedProcessModalPage')
)
const DeleteProcessModalPage = lazy(
  () => import('modules/processes/pages/Modals/DeleteProcessModalPage')
)
const DuplicateProcessModalPage = lazy(
  () => import('modules/processes/pages/Modals/DuplicateProcessModalPage')
)
const DuplicateStepModalPage = lazy(
  () => import('modules/processes/pages/Modals/DuplicateStepModalPage')
)
const EditProcessModalPage = lazy(
  () => import('modules/processes/pages/Modals/EditProcessModalPage')
)
const EditStepModalPage = lazy(() => import('modules/processes/pages/Modals/EditStepModalPage'))

const MarkAsCompletedMaterializedProcessModalPage = lazy(
  () => import('modules/processes/pages/Modals/MarkAsCompletedMaterializedProcessModalPage')
)

const MaterializeProcessModalPage = lazy(
  () => import('modules/processes/pages/Modals/MaterializeProcessModalPage')
)
const ProcessFromTemplateWizardPage = lazy(
  () => import('modules/processes/pages/Modals/ProcessFromTemplateWizardPage')
)
const SendTaskRemindersModalPage = lazy(
  () => import('modules/processes/pages/Modals/SendTaskRemindersModalPage')
)
const EmployeeProcessPage = lazy(
  () => import('modules/processes/pages/Process/EmployeeProcessPage')
)
const ProcessOverviewPage = lazy(
  () => import('modules/processes/pages/Process/ProcessOverviewPage')
)
const ProcessRootPage = lazy(() => import('modules/processes/pages/Process/ProcessRootPage'))
const ProcessSettingsPage = lazy(
  () => import('modules/processes/pages/Process/ProcessSettingsPage')
)
const TemplatePage = lazy(() => import('modules/processes/pages/Processes/TemplatePage'))

const RootPage = lazy(() => import('modules/processes/pages/RootPage'))

export const processesRoutes: NavigationRoutesFn = (navigation) => {
  const overviewRoute = navigation.processes.template.id.overview

  const processHeaderMeta = ({ params, watchQuery }: MetaContext) => {
    if (!params['id']) return { title: '' }
    return watchQuery({
      query: GetProcessDetailDocument,
      variables: {
        id: String(params['id']),
      },
    }).map((result) => {
      const process = result?.data?.processes?.process
      if (!process) return { title: '' }

      const editPath = navigation.processes.template.id.settings.edit.fullPath({
        category: String(result?.data?.processes?.process?.category),
        id: result?.data?.processes?.process?.id ?? '0',
      })

      return {
        title: process.name,
        headerComponent: <ProcessResourceHeader editPath={editPath} process={process} />,
      }
    })
  }

  return [
    buildModuleRoute({
      path: navigation.processes.path,
      element: <RootPage />,
      handle: {
        module: 'processes',
        moduleName: () => i18n.t('processes.breadcrumbs.workflows_title'),
        meta: () => ({
          title: i18n.t('processes.breadcrumbs.workflows_title'),
          description: i18n.t('processes.breadcrumbs.workflows_description'),
          illustration: 'automations',
          subNavigation: [
            {
              id: 'navigation.processes.fullPath',
              title: i18n.t('processes.breadcrumbs.workflows_title'),
              href: navigation.processes.fullPath(),
              index: true,
            },
            {
              id: 'navigation.processes.surveys.fullPath',
              title: i18n.t('processes.tabs.surveys'),
              href: navigation.processes.surveys.fullPath(),
            },
          ],
        }),
      },
      children: [
        {
          index: true,
          element: <CategoryLayout />,
        },
        ...surveysRoutes(navigation),
        {
          path: navigation.processes.template.path,

          handle: {
            meta: ({ params }) => {
              const labels: { [key: string]: string } = {
                custom: i18n.t('processes.common.tag.custom'),
                onboarding: i18n.t('processes.common.tag.onboarding'),
                offboarding: i18n.t('processes.common.tag.offboarding'),
              }

              const category = params['category'] || ''
              const categoryName = category in labels ? labels[category] : category
              return {
                title: capitalize(categoryName),
                description: i18n.t('processes.breadcrumbs.workflows_description'),
                illustration: 'automations',
              }
            },
          },

          children: [
            {
              path: navigation.processes.template.create.path,

              handle: {
                meta: () => ({
                  title: i18n.t('processes.breadcrumbs.from_template'),
                  description: i18n.t('processes.breadcrumbs.workflows_description'),
                  illustration: 'automations',
                }),
              },
              element: <ProcessFromTemplateWizardPage />,
            },
            {
              path: navigation.processes.template.new.path,

              handle: {
                meta: ({ params }) => ({
                  title: i18n.t('processes.breadcrumbs.new_workflow_from_scratch', {
                    name: params['category'],
                  }),
                  description: i18n.t('processes.breadcrumbs.workflows_description'),
                  illustration: 'automations',
                }),
              },
              element: <CreateCustomProcessModalPage />,
            },
            {
              path: navigation.processes.template.id.path,

              handle: {
                meta: ({ params, watchQuery }) => {
                  if (!params['id']) return { title: '' }
                  return watchQuery({
                    query: GetProcessDetailDocument,
                    variables: {
                      id: String(params['id']),
                    },
                  }).map((result) => ({
                    title: `${result?.data?.processes?.process?.name}`,
                    description: '',
                  }))
                },
              },

              children: [
                {
                  index: true,
                  element: <Navigate to={overviewRoute.path} replace />,
                },
                {
                  path: navigation.processes.template.id.overview.path,

                  children: [
                    {
                      path: navigation.processes.template.id.overview.sendReminder.path,
                      element: <SendTaskRemindersModalPage />,
                    },
                  ],
                  handle: {
                    resource: true,
                    meta: processHeaderMeta,
                  },
                  element: <ProcessOverviewPage />,
                },
                {
                  path: navigation.processes.template.id.employees.path,
                  handle: {
                    resource: true,
                    meta: processHeaderMeta,
                  },

                  children: [
                    {
                      path: navigation.processes.template.id.employees.new.path,

                      handle: {
                        meta: () => ({
                          title: i18n.t('processes.breadcrumbs.new'),
                          description: '',
                        }),
                      },
                      element: <MaterializeProcessModalPage />,
                    },
                    {
                      path: navigation.processes.template.id.employees.removeMaterialized.path,

                      handle: {
                        meta: () => ({
                          title: i18n.t('processes.breadcrumbs.delete'),
                          description: '',
                        }),
                      },
                      element: <DeleteMaterializedProcessModalPage />,
                    },
                    {
                      path: navigation.processes.template.id.employees.updateStatus.path,

                      handle: {
                        meta: () => ({
                          title: i18n.t('processes.breadcrumbs.complete'),
                          description: '',
                        }),
                      },
                      element: <MarkAsCompletedMaterializedProcessModalPage />,
                    },
                    {
                      path: navigation.processes.template.id.settings.edit.path,

                      handle: {
                        meta: () => ({
                          title: i18n.t('processes.breadcrumbs.edit'),
                          description: i18n.t('processes.breadcrumbs.workflows_description'),
                          illustration: 'automations',
                        }),
                      },
                      element: <EditProcessModalPage />,
                    },
                  ],
                  element: <EmployeeProcessPage />,
                },
                {
                  path: navigation.processes.template.id.settings.path,

                  handle: {
                    resource: true,
                    meta: processHeaderMeta,
                  },

                  children: [
                    {
                      path: navigation.processes.template.id.settings.createStep.path,

                      handle: {
                        meta: () => ({
                          title: i18n.t('processes.breadcrumbs.add_step'),
                          description: '',
                        }),
                      },
                      element: <AddStepModalPage />,
                    },
                    {
                      path: navigation.processes.template.id.settings.editStep.path,

                      handle: {
                        meta: () => ({
                          title: i18n.t('processes.breadcrumbs.edit'),
                          description: '',
                        }),
                      },
                      element: <EditStepModalPage />,
                    },
                    {
                      path: navigation.processes.template.id.settings.duplicate.path,

                      handle: {
                        meta: () => ({
                          title: i18n.t('processes.breadcrumbs.duplicate_step'),
                          description: '',
                        }),
                      },
                      element: <DuplicateStepModalPage />,
                    },
                    {
                      path: navigation.processes.template.id.settings.edit.path,

                      handle: {
                        meta: () => ({
                          title: i18n.t('processes.breadcrumbs.edit'),
                          description: i18n.t('processes.breadcrumbs.workflows_description'),
                          illustration: 'automations',
                        }),
                      },
                      element: <EditProcessModalPage />,
                    },
                  ],
                  element: <ProcessSettingsPage />,
                },
                {
                  path: navigation.processes.template.id.processInstance.path,

                  handle: {
                    meta: ({ params, watchQuery }) => {
                      const id = params['materializedProcessId']
                      if (!id) return { title: '' }

                      return watchQuery({
                        query: MaterializedProcessDocument,
                        variables: {
                          id: String(id),
                        },
                      }).map((result) => ({
                        title: `${result?.data?.processes?.materializedProcessesConnection?.nodes[0]?.name}`,
                        description: '',
                      }))
                    },
                  },

                  children: [
                    {
                      path: navigation.processes.template.id.processInstance.completeProcess.path,

                      handle: {
                        meta: () => ({
                          title: i18n.t('processes.breadcrumbs.complete'),
                          description: '',
                        }),
                      },
                      element: <CompletedMaterializedProcessPage />,
                    },
                    {
                      path: navigation.processes.template.id.processInstance.item.path,

                      handle: {
                        meta: () => ({
                          title: i18n.t('processes.breadcrumbs.detail_item'),
                          description: '',
                        }),
                      },
                      element: <ItemDetailPage />,
                    },
                  ],
                  element: <MaterializedProcessPage />,
                },
              ],
              element: <ProcessRootPage />,
            },
            {
              path: navigation.processes.template.edit.path,

              handle: {
                meta: () => ({
                  title: i18n.t('processes.breadcrumbs.edit'),
                  description: i18n.t('processes.breadcrumbs.workflows_description'),
                  illustration: 'automations',
                }),
              },
              element: <EditProcessModalPage />,
            },
            {
              path: navigation.processes.template.duplicate.path,

              handle: {
                meta: () => ({
                  title: i18n.t('processes.breadcrumbs.duplicate'),
                  description: i18n.t('processes.breadcrumbs.workflows_description'),
                  illustration: 'automations',
                }),
              },
              element: <DuplicateProcessModalPage />,
            },
            {
              path: navigation.processes.template.delete.path,

              handle: {
                meta: () => ({
                  title: i18n.t('processes.breadcrumbs.delete'),
                  description: i18n.t('processes.breadcrumbs.workflows_description'),
                  illustration: 'automations',
                }),
              },
              element: <DeleteProcessModalPage />,
            },
          ],
          element: <TemplatePage />,
        },
      ],
    }),
  ]
}
